import { canonicalFileSchema } from "@/lib/data/schemas/files";
import {
  CitySchema,
  CountrySchema,
  PhoneNumberSchema,
  StateSchema,
  StreetAddressSchema,
  ZipCodeSchema,
} from "@/lib/data/schemas/CommonSchemaFields";
import { StepIdSchema } from "@/components/onboarding/OnboardingStepProvider";
import {
  BaseContactSchema,
  ContactAddressSchema,
  ContactEmailSchema,
  ContactFaxSchema,
  ContactPhoneSchema,
  ContactTagConnectionSchema,
  ContactWebsiteSchema,
} from "@/lib/data/schemas/contact/ContactSchema";
import {
  EmbeddedUserSchema,
  UserUserGroupSchema,
} from "@/lib/data/schemas/firm/FirmUserSchema";
import { commonFirmFields } from "@/lib/data/schemas/firm/FirmCommonFields";
import {
  MATTER_CONTACT_CONNECTION_FAULT_STATUS_OPTIONS,
  MATTER_INSURANCE_COVERAGE_STATUS_OPTIONS,
  NOT_APPLICABLE_INSURANCE_COVERAGE,
} from "@/lib/data/static/matters/insuranceCoverageStatics";
import {
  MATTER_CONTACT_CONNECTION_LABEL_OPTIONS,
  MATTER_CONTACT_CONNECTION_WITNESS_STATUS_OPTIONS,
} from "@/lib/data/static/matterStatics";
import {
  CONTACT_INDIVIDUAL_SUBTYPES,
  CONTACT_ORGANIZATION_SUBTYPES,
} from "@/lib/data/static/contactStatics";
import { z } from "zod";

export {
  EmbeddedUserSchema,
  PermissionCatalogDefinitionSchema,
  PermissionGrantSchema,
  PermissionGroupActionSchema,
  PermissionGroupSchema,
  PermissionValueSchema,
  UserFirmSchema,
  UserGroupPermissionsSchema,
  UserNotificationPreferencesSchema,
  UserSchema,
  UserUserGroupSchema,
} from "@/lib/data/schemas/firm/FirmUserSchema";

export const FirmSchema = z.object({
  id: z.string(),
  name: z
    .string()
    .trim()
    .max(100, { message: "Firm name must be 100 characters or less" }),
  description: z
    .string()
    .trim()
    .max(5000, { message: "Description must be 5000 characters or less" })
    .nullable()
    .optional(),
  onboardingProgress: StepIdSchema.or(z.literal("COMPLETED"))
    .nullable()
    .optional(),
  colorTheme: z.enum(["default", "green", "orange", "purple"]).nullable(),
  profilePicturePresignedUrlKey: z.string().nullable().optional(),
  dateCreated: z.string().nullable(),
  dateModified: z.string().nullable(),
});

export const FirmPhoneSchema = z.object({
  id: z.string(),
  firmId: z.string(),
  friendlyName: z.string().nullable(),
  label: z
    .string()
    .min(1, "Label is required")
    .max(100, { message: "Label must be 100 characters or less" }),
  description: z.string().nullable(),
  number: z.string().min(1, "Phone number is required"),
  sid: z.string().nullable(),
  dateCreated: z.string().nullable(),
  dateModified: z.string().nullable(),
});

export const SearchAvailablePhoneNumbersSchema = z.object({
  areaCode: z.string().min(3, "Area code must be 3 digits").or(z.literal("")),
  locality: z.string().optional(),
  pattern: z.string().optional(),
  postalCode: z
    .string()
    .min(5, "Postal code must be at least 5 characters")
    .or(z.literal("")),
  region: z.string().optional(),
});

export const AvailablePhoneNumberSchema = z.object({
  friendlyName: z.string(),
  lata: z.string().nullable(),
  locality: z.string().nullable(),
  number: z.string(),
  postalCode: z.string().nullable(),
  rateCenter: z.string().nullable(),
  region: z.string().nullable(),
});

export const FirmOfficeSchema = z.object({
  id: z.string(),
  label: z.string().trim().min(1, { message: "Label is required" }).max(50, {
    message: "Label is too long",
  }),
  description: z.string().trim().nullable().optional(),
  phoneNumber: PhoneNumberSchema.nullable().optional(),
  street1: StreetAddressSchema,
  street2: StreetAddressSchema.nullable().optional().or(z.literal("")),
  city: CitySchema,
  state: StateSchema,
  zipCode: ZipCodeSchema,
  country: CountrySchema,
  firmId: z.string(),
  dateCreated: z.string(),
  dateModified: z.string(),
});

const FirmLeadSourceStatusCategorySchema = z.object({
  dateCreated: z.string().nullish(),
  dateModified: z.string().nullish(),
  description: z.string().nullish(),
  id: z.string(),
  label: z.string(),
});

export const FirmLeadSourceStatusSchema = z.object({
  id: z.string(),
  category: FirmLeadSourceStatusCategorySchema,
  categoryId: z.string(),
  defaultAttorneyFee: z.number().nullable().optional(),
  description: z.string().nullable().optional(),
  goal: z.number().nullable().optional(),
  label: z.string(),
  order: z.number().optional(),
});

export const FirmLeadSourceSchema = z
  .object({
    id: z.string(),
    description: z
      .string()
      .max(5000, { message: "Description must be 5000 characters or less" })
      .nullable()
      .optional(),
    label: z
      .string()
      .trim()
      .min(1, { message: "Label is required" })
      .max(100, { message: "Label must be 100 characters or less" }),
    status: FirmLeadSourceStatusSchema.nullable().optional(),
    statusId: z.string().min(1, { message: "Matter status is required" }),
    webhookToken: z.string().nullable().optional(),
  })
  .merge(commonFirmFields);

export const FirmVirtualBusinessCardSchema = z
  .object({
    id: z.string(),
    s3Key: z.string(),
    phoneNumber: z.string(),
    fullName: z
      .string()
      .max(100, { message: "Name must be 100 characters or less" }),
    phoneId: z.string(),
    city: z.string(),
    country: z.string(),
    email: z.string(),
    state: StateSchema,
    street1: StreetAddressSchema,
    street2: StreetAddressSchema.optional().or(z.literal("")),
    website: z.string(),
    zipCode: ZipCodeSchema,
  })
  .merge(commonFirmFields);

export const FirmFileSchema = z
  .object({
    fileContentId: z.string().uuid().nullable().optional(),
    id: z.string(),
    description: z
      .string()
      .max(5000, { message: "Description must be 5000 characters or less" })
      .nullable()
      .optional(),
    name: z
      .string()
      .trim()
      .min(1, { message: "Name is required" })
      .max(100, { message: "Name must be 100 characters or less" })
      .refine((val) => !val.includes("/"), {
        message: "cannot contain forward slashes (/)",
      }),
    idLocation: z.string().nullable().optional(),
    isFolder: z.boolean(),
    presignedUrlKey: z.string().nullable().optional(),
    tags: z.array(z.string()).nullable().optional(),
    variables: z.array(z.string()).nullable().optional(),
  })
  .merge(commonFirmFields)
  .merge(
    canonicalFileSchema
      .pick({
        revision: true,
        currentVersionId: true,
        currentVersionNumber: true,
        hasPreviousVersions: true,
        capabilities: true,
        contentType: true,
        sizeBytes: true,
      })
      .partial(),
  );

export const FirmContactConnectionSchema = z
  .object({
    firmContactConnectionId: z.string().optional(),
    firmContactConnectionContactAddress: z
      .array(ContactAddressSchema)
      .optional(),
    firmContactConnectionContactEmail: z.array(ContactEmailSchema).optional(),
    firmContactConnectionContactFax: z.array(ContactFaxSchema).optional(),
    firmContactConnectionContactId: z.string().optional(),
    firmContactConnectionContactIndividualSubTypes: z
      .array(z.enum(CONTACT_INDIVIDUAL_SUBTYPES))
      .optional(),
    firmContactConnectionContactName: z.string().optional(),
    firmContactConnectionContactOrganizationSubTypes: z
      .array(z.enum(CONTACT_ORGANIZATION_SUBTYPES))
      .optional(),
    firmContactConnectionContactPhone: z.array(ContactPhoneSchema).optional(),
    firmContactConnectionContactTagConnection: z
      .array(ContactTagConnectionSchema)
      .optional(),
    firmContactConnectionContactType: BaseContactSchema.shape.type.optional(),
    firmContactConnectionContactWebsite: z
      .array(ContactWebsiteSchema)
      .optional(),
    firmContactConnectionDateTimeEnd: z.string().optional(),
    firmContactConnectionDateTimeStart: z.string().optional(),
    firmContactConnectionDescription: z.string().optional(),
    firmContactConnectionFaultStatus: z
      .enum(MATTER_CONTACT_CONNECTION_FAULT_STATUS_OPTIONS)
      .optional(),
    firmContactConnectionInsuranceCoverage: z
      .enum(MATTER_INSURANCE_COVERAGE_STATUS_OPTIONS)
      .or(z.literal(NOT_APPLICABLE_INSURANCE_COVERAGE))
      .nullable()
      .optional(),
    firmContactConnectionLabel: z
      .enum(MATTER_CONTACT_CONNECTION_LABEL_OPTIONS)
      .readonly()
      .or(z.literal(""))
      .optional(),
    firmContactConnectionShareFolderPasscode: z.string().optional(),
    firmContactConnectionWitnessStatus: z
      .enum(MATTER_CONTACT_CONNECTION_WITNESS_STATUS_OPTIONS)
      .optional(),
  })
  .merge(commonFirmFields);

export const FirmUserConnectionSchema = z
  .object({
    firmUserConnectionId: z.string().optional(),
    firmUserConnectionLabel: z.string().optional(),
  })
  .merge(commonFirmFields)
  .extend({
    user: EmbeddedUserSchema.nullish(),
  });

export const PublisherAppSchema = z.object({
  id: z.string(),
  description: z.string().nullable(),
  name: z.string().nullable(),
  status: z.string().nullable(),
  tags: z.array(z.string()).nullable(),
  publisherId: z.string().nullable(),
  dateCreated: z.string().nullable(),
  dateModified: z.string().nullable(),
});

export const AppSubscriptionSchema = z.object({
  id: z.string(),
  firmId: z.string(),
  appId: z.string(),
  app: PublisherAppSchema.optional(),
  dateCreated: z.string().nullable(),
  dateModified: z.string().nullable(),
});

export const LeadSourceMappingSchema = z
  .object({
    id: z.string(),
    leadSourceId: z.string(),
    key: z.string().trim().min(1, {
      message: "Outside key is required",
    }),
    targetId: z.string(),
    target: z.object({
      id: z.string(),
      displayItemLabel: z.string(),
      displayAttributeLabel: z.string(),
      displayOrder: z.number(),
      item: z.string(),
      attribute: z.string(),
    }),
    // Compatibility fields returned by the API. New writes use targetId.
    item: z.string(),
    attribute: z.string(),
    dateCreated: z.string(),
    dateModified: z.string(),
  })
  .extend({
    firmId: z.string().optional(),
  });

export const MappingTargetSchema = z.object({
  id: z.string(),
  displayItemLabel: z.string(),
  displayAttributeLabel: z.string(),
  displayOrder: z.number(),
  item: z.string(),
  attribute: z.string(),
});

export const UserGroupSchema = UserUserGroupSchema;
